import React, { useEffect } from "react";
import { Box, Grid, Typography } from "@mui/material";
import CustomPasswordTextField from "../../common/CustomPasswordTextField";
import CustomTextField from "../../common/CustomTextField";
import { useForm } from "react-hook-form";
import { ResetPassword, resetPassword } from "../../../redux/slice/userSlice";
import Button from "../../common/Button";
import { LockOutlined, SaveOutlined } from "@mui/icons-material";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { SingleAlertInfo, setSingleAlertObj } from "../../../redux/slice/commonSlice";
import { useAuthSlice } from "../../../redux/slice/authSlice";
import { PayloadAction, SerializedError } from "@reduxjs/toolkit";
import { useNavigation } from "../../../hooks/useNavigation";
import { SectionHeader } from "../../ui/ContentSection";
import { useModalStyles } from "../../common/useModalStyles";

interface ChangePasswordProps {
  // When rendered inside AccountProfile's sidebar shell card, skip the
  // extra bordered/shadowed wrapper below — it's only needed when this
  // component is the whole page (the standalone /change_password route).
  embedded?: boolean;
}

const ChangePassword: React.FC<ChangePasswordProps> = ({ embedded = false }) => {
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ResetPassword>();

  const dispatch = useAppDispatch();
  const { navigate } = useNavigation();
  const { userInfo } = useAuthSlice();
  const { tokens: mTokens, isDark, primaryBtnSx, fieldWrapSx } = useModalStyles();

  useEffect(() => {
    setValue("userId", userInfo.userId);
    setValue("email", userInfo.userName);
    setValue("passwordResetRequired", false);
  }, [setValue, userInfo]);

  const onSubmit = async (data: ResetPassword) => {
    if (data.oldPassword === data.password) {
      const alertObj: SingleAlertInfo = {
        message: "Old password cannot be the new password.",
        alertType: "error",
      };
      dispatch(setSingleAlertObj(alertObj));
      return;
    }

    try {
      const response = await dispatch(resetPassword(data));

      if (response.meta.requestStatus === "fulfilled") {
        navigate("/");
        const alertObj: SingleAlertInfo = {
          message: "Password Reset Successfully",
          alertType: "success",
        };
        dispatch(setSingleAlertObj(alertObj));
      } else if (response.meta.requestStatus === "rejected") {
        const error = (response as PayloadAction<unknown, string, any, SerializedError>).error;

        if (error?.code) {
          const alertObj: SingleAlertInfo = {
            message: "Password Reset Failed",
            alertType: "error",
          };
          dispatch(setSingleAlertObj(alertObj));
        }
      }
      return response;
    } catch (error) {
      console.error("Error resetting password:", error);
      throw error;
    }
  };

  const cardSx = {
    borderRadius: "16px",
    border: `1px solid ${mTokens.border}`,
    backgroundColor: mTokens.bg,
    backgroundImage: "none",
    overflow: "hidden" as const,
    boxShadow: isDark ? "0 10px 20px rgba(0,0,0,0.1)" : "none",
  };

  const FieldLabel = ({ label, required = false }: { label: string; required?: boolean }) => (
    <Typography
      sx={{
        fontSize: "0.8rem",
        fontWeight: 600,
        color: mTokens.textSecondary,
        mb: 0.7,
        display: "flex",
        alignItems: "center",
        gap: 0.4,
      }}
    >
      {label}
      {required && <Box component="span" sx={{ color: mTokens.accent }}>*</Box>}
    </Typography>
  );

  const fieldSx = (hasError = false) => ({
    ...fieldWrapSx(hasError),
    "& .MuiIconButton-root": { color: mTokens.textSecondary },
    "& .MuiIconButton-root:hover": {
      color: mTokens.accent,
      backgroundColor: isDark ? "rgba(20,184,166,0.12)" : "rgba(20,184,166,0.08)",
    },
  });

  const header = embedded ? (
    <Box display="flex" alignItems="center" gap={1.4} mb={2.5}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "11px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: `${mTokens.accent}14`,
          color: mTokens.accent,
          flexShrink: 0,
        }}
      >
        <LockOutlined fontSize="small" />
      </Box>
      <Box>
        <Typography sx={{ fontWeight: 800, color: mTokens.textPrimary, fontSize: "1.1rem", lineHeight: 1.2 }}>
          Change Password
        </Typography>
        <Typography sx={{ fontSize: "0.78rem", color: mTokens.textSecondary, mt: 0.15 }}>
          Update the password used to sign in to your account
        </Typography>
      </Box>
    </Box>
  ) : (
    <SectionHeader icon={<LockOutlined fontSize="small" />} title="Change Password" />
  );

  const formBody = (
    <Box sx={embedded ? undefined : { px: { xs: 2, md: 2.8 }, py: 2.5 }}>
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <FieldLabel label="Email" required />
                <Box sx={fieldSx(!!errors.email)}>
                  <CustomTextField
                    control={control}
                    controllerName="email"
                    label=""
                    rules={{ required: "Email is required." }}
                    disabled={true}
                    size="small"
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <FieldLabel label="Current Password" required />
                <Box sx={fieldSx(!!errors.oldPassword)}>
                  <CustomPasswordTextField
                    control={control}
                    controllerName="oldPassword"
                    label=""
                    size="small"
                    rules={{ required: "Current Password is required." }}
                    inputProps={{ autoComplete: "new-password" }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <FieldLabel label="New Password" required />
                <Box sx={fieldSx(!!errors.password)}>
                  <CustomPasswordTextField
                    control={control}
                    controllerName="password"
                    label=""
                    size="small"
                    rules={{
                      required: "New Password is required.",
                      minLength: {
                        value: 12,
                        message: "Password should be at least 12 characters.",
                      },
                      pattern: {
                        value: /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])/,
                        message:
                          "Password must contain at least one uppercase letter, one number, and one special character",
                      },
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <FieldLabel label="Confirm Password" required />
                <Box sx={fieldSx(!!errors.confirmPassword)}>
                  <CustomPasswordTextField
                    control={control}
                    controllerName="confirmPassword"
                    label=""
                    size="small"
                    rules={{
                      required: "Confirmation of password is required.",
                      validate: (value: any) =>
                        value === watch("password") || "Passwords do not match",
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
            <Box
              display="flex"
              justifyContent="flex-end"
              flexDirection={{ xs: "column", sm: "row" }}
              gap={1.2}
              sx={{ mt: 2.5 }}
            >
              <Button
                type="submit"
                startIcon={<SaveOutlined />}
                fullWidth
                removeUpperCase
                sx={{ ...primaryBtnSx, minWidth: { sm: 140 }, width: { xs: "100%", sm: "auto" } }}
              >
                Save
              </Button>
            </Box>
          </form>
    </Box>
  );

  if (embedded) {
    return (
      <Box sx={{ width: "100%" }}>
        {header}
        {formBody}
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 480, width: "100%", mx: "auto" }}>
      <Box sx={cardSx}>
        {header}
        {formBody}
      </Box>
    </Box>
  );
};

export default ChangePassword;
